import Spine from '@finviz/spine'
import merge from 'lodash.merge'

import { TodoObjectHashAnyType } from '../../types/shared'
import { drawingToolsByType } from '../canvas/drawingTools'
import CanvasElement from '../canvas/element'
import { CanvasElementType } from '../constants/common'
import { getIsSSr, getUuid } from '../utils/helpers'
import Pane from './pane'
import { changeElementZIndexes } from './utils'

class Element extends Spine.Model {
  static initClass(paneModel: typeof Pane) {
    this.configure('Element', 'instance', 'lastChange', 'zIndex', 'elementId')
    this.belongsTo('pane', paneModel)
  }

  declare instance: CanvasElement
  declare pane_id: string
  declare destroyed: true | undefined
  declare pane: () => Pane
  lastChange: number | null = null
  elementId: string | null = null
  zIndex = -1

  generateUuid = getIsSSr() ? undefined : getUuid

  constructor(...args: any[]) {
    super(...args)
    const elementAttrs = args[0]
    this.elementId = elementAttrs?.elementId ?? this.generateUuid?.()
    this.one('create', () => {
      this.updateAttributes({
        zIndex: elementAttrs?.zIndex ?? this.pane().getElementZIndexRange().max + 1,
      })
    })
  }

  replace(instance: CanvasElement) {
    const oldInstance = this.instance
    this.updateAttribute('instance', instance)
    this.trigger('replace', this, oldInstance)
  }

  makeClone() {
    const { instance, pane_id } = this
    const { attrs } = instance
    const { scale } = instance.model
    const clonedObj = merge({ pane_id }, attrs)
    const cloned = drawingToolsByType[instance.type as CanvasElementType].fromObject(clonedObj, instance.model)
    const { ThumbSettings } = this.pane().getChartLayoutSettings()
    const offset = ThumbSettings.size + ThumbSettings.borderWidth * 2
    cloned.moveBy(scale.x.invert(offset), scale.y.invert(offset) - scale.y.invert(0))
    cloned.cachePointPositionTimestamp()
    Element.create({ instance: cloned, pane_id })
    this.trigger('makeClone', this, cloned)
  }

  moveToZIndex(zIndex: number) {
    const currentZIndex = this.zIndex
    const directionCoef = zIndex > currentZIndex ? 1 : -1
    const newZIndex = Math.abs(zIndex) > 0 ? zIndex : (zIndex + 1) * directionCoef // 0 is protected value for chart element
    const { min, max } = this.pane().getElementZIndexRange()
    if (
      currentZIndex === newZIndex ||
      (currentZIndex === min && directionCoef === -1) ||
      (currentZIndex === max && directionCoef === 1)
    ) {
      return
    }
    const { elementsBelowZero, elementsAboveZero } = this.pane().getBelowAboveZeroElements()

    changeElementZIndexes({
      elementsArray: elementsBelowZero,
      addElement: newZIndex < 0 ? this : undefined,
      oldZIndex: currentZIndex,
      newZIndex,
      isBelowZero: true,
    })

    changeElementZIndexes({
      elementsArray: elementsAboveZero,
      addElement: newZIndex > 0 ? this : undefined,
      oldZIndex: currentZIndex,
      newZIndex,
    })
  }

  bringToFront() {
    this.moveToZIndex(this.pane().getElementZIndexRange().max + 1)
  }

  sendToBack() {
    this.moveToZIndex(this.pane().getElementZIndexRange().min - 1)
  }

  bringForward() {
    this.moveToZIndex(this.zIndex + 1)
  }

  sendBackward() {
    this.moveToZIndex(this.zIndex - 1)
  }

  isChart() {
    return !!this.instance?.type.startsWith('charts/')
  }

  isIndicator() {
    return !!this.instance?.type.startsWith('indicators/')
  }

  isOverlay() {
    return !!this.instance?.type.startsWith('overlays/')
  }

  isDrawing() {
    return !!this.instance?.type.startsWith('canvas/')
  }

  isMouseDown() {
    return !!this.instance?.isMouseDown
  }

  toObject() {
    return {
      ...this.instance.toObject(),
      elementId: this.elementId,
      zIndex: this.zIndex,
    }
  }

  toConfig() {
    return {
      ...this.instance.toConfig(),
      elementId: this.elementId,
      zIndex: this.zIndex,
    }
  }

  destroyCascade(options?: TodoObjectHashAnyType) {
    return this.destroy(options)
  }

  refreshElementId() {
    this.updateAttribute('elementId', this.generateUuid?.())
  }
}

export default Element
